/**
 * Article research citations — FRIDGE-116.
 *
 * Renders the first-party research an article has earned a citation to, in the
 * article flow after the FAQ. Template injection only: article HTML in the
 * database is never modified, so nothing here can drift out of sync with the
 * datasets the way an injected string would.
 *
 * Every line is fed by `resolveResearchCitations()`, which returns nothing at
 * all unless a real dataset row matched the article's subject — so this
 * component renders nothing rather than a placeholder, and cannot state a
 * figure that is not in the data. See lib/research-citations.ts.
 *
 * Visible to readers, not hidden for crawlers. FRIDGE-119 removed the hidden
 * block that restated the article for machines; the fix for citations is the
 * opposite move — put the evidence on the page where a reader can follow it.
 */

import Link from 'next/link'
import type { ResearchCitation } from '@/lib/research-citations'

export function ArticleResearchCitations({
  citations,
}: {
  citations?: ResearchCitation[] | null
}) {
  if (!citations || citations.length === 0) return null

  return (
    <section
      aria-labelledby="article-research-sources"
      style={{
        marginTop: 48,
        background: 'var(--gray-50, #f9fafb)',
        border: '1px solid var(--gray-200, #e5e7eb)',
        borderRadius: 8,
        padding: '24px 24px 20px',
      }}
    >
      <h2
        id="article-research-sources"
        style={{ fontSize: 20, marginBottom: 6, color: '#111827' }}
      >
        From Fridge.com research
      </h2>
      <p style={{ fontSize: 14, color: '#4b5563', marginBottom: 18 }}>
        Figures below come from data Fridge.com builds and maintains. Each links
        to the record behind it.
      </p>

      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {citations.map((citation) => (
          <li
            key={citation.kind}
            style={{
              paddingTop: 14,
              borderTop: '1px solid var(--gray-200, #e5e7eb)',
              marginTop: 14,
            }}
          >
            <p
              style={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: '#6b7280',
                marginBottom: 6,
              }}
            >
              {citation.source}
            </p>
            <p style={{ fontSize: 15, lineHeight: 1.6, color: '#1f2937', marginBottom: 8 }}>
              {citation.fact}
            </p>
            <p style={{ fontSize: 14, margin: 0 }}>
              <Link
                href={citation.href}
                style={{ color: '#1d4ed8', fontWeight: 600, textDecoration: 'none' }}
              >
                See the data
              </Link>
              {citation.methodHref && (
                <>
                  <span style={{ color: '#9ca3af', padding: '0 8px' }}>·</span>
                  <Link
                    href={citation.methodHref}
                    style={{ color: '#4b5563', textDecoration: 'none' }}
                  >
                    How this is measured
                  </Link>
                </>
              )}
            </p>
          </li>
        ))}
      </ul>
    </section>
  )
}
